/**
 * Transient, promise-based dialogs.
 *
 * The panels in this folder (settings, import, export…) own persistent
 * `<dialog>` elements because they are driven by `state.runtime.dialog`. This
 * file covers the other half of the problem: one-off dialogs that are created,
 * awaited and thrown away - "are you sure?", "give me a reason", or an ad-hoc
 * content modal raised from the middle of a handler.
 *
 * Putting them here means every throwaway dialog in Lunchly shares one focus
 * trap, one Escape/backdrop contract and one piece of markup, instead of each
 * caller re-inventing (and half-implementing) modal accessibility.
 */

import { append, el, field, focusableWithin, on, trapFocus } from '../dom.js';
import { icon } from '../icons.js';

/** Monotonic counter so every generated id is unique across stacked modals. */
let sequence = 0;

const SIZE_CLASS = {
  narrow: 'dialog--narrow',
  medium: '',
  wide: 'dialog--wide',
};

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

function nextId(prefix) {
  sequence += 1;
  return `${prefix}-${sequence}`;
}

/**
 * First control the user should land on inside a dialog body.
 *
 * `focusableWithin` filters on `offsetParent`, which is the right thing in a
 * browser (it skips genuinely hidden controls) but is always null in a
 * layout-less DOM such as jsdom. The plain selector is the fallback so a
 * freshly built dialog body still receives focus under test.
 */
function firstFocusableInBody(body) {
  return focusableWithin(body)[0] ?? body.querySelector(FOCUSABLE);
}

/**
 * Open a transient modal dialog and return a handle to it.
 *
 * Why a factory rather than a component: these dialogs have no place in
 * `state.runtime.dialog` - they are a *question asked of the user in the middle
 * of an action*, so the caller wants to `await` an answer rather than re-render
 * from state. The node is created, appended to `document.body`, shown with
 * `showModal()` and removed again the moment it closes, so nothing accumulates
 * in the DOM.
 *
 * Escape (the native `cancel` event) and a click on the backdrop both dismiss
 * the dialog with `null`, and focus always returns to whatever was focused
 * before it opened.
 *
 * @param {object} options
 * @param {string} options.title - heading text; also the dialog's accessible name.
 * @param {string} [options.description] - optional sub-heading under the title.
 * @param {Node|string|Array} [options.content] - body content, built by the caller.
 * @param {Array<{
 *   label: string,
 *   value?: *,
 *   className?: string,
 *   disabled?: boolean,
 *   autofocus?: boolean,
 *   onSelect?: (api: { close: Function, element: HTMLElement, body: HTMLElement }) => *,
 * }>} [options.actions] - footer buttons, left to right. Choosing one closes the
 *   dialog with its `value`; an `onSelect` that returns exactly `false` keeps it open.
 * @param {'narrow'|'medium'|'wide'} [options.size='narrow']
 * @returns {{ element: HTMLDialogElement, body: HTMLElement, buttons: HTMLButtonElement[],
 *   close: (value?: *) => void, closed: Promise<*> }}
 */
export function openModal({
  title = '',
  description = '',
  content = null,
  actions = [],
  size = 'narrow',
} = {}) {
  const id = nextId('modal');
  const headingId = `${id}-heading`;
  const descriptionId = `${id}-description`;
  const previouslyFocused = document.activeElement;

  const disposers = [];
  let outcome = null;
  let disposed = false;
  let resolveClosed;
  const closed = new Promise((resolve) => {
    resolveClosed = resolve;
  });

  /** Single teardown point: every close route ends up here exactly once. */
  function teardown() {
    if (disposed) return;
    disposed = true;
    for (const dispose of disposers) dispose();
    disposers.length = 0;
    element.remove();
    // Returning focus is the whole reason we captured `previouslyFocused`: a
    // keyboard user must land back on the control that opened the dialog.
    if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
      previouslyFocused.focus();
    }
    resolveClosed(outcome);
  }

  function close(value = null) {
    if (disposed) return;
    outcome = value;
    if (element.open) element.close();
    else teardown();
  }

  const heading = el('h2', { class: 'dialog__heading', id: headingId, text: String(title ?? '') });
  const descriptionNode = description
    ? el('p', { class: 'dialog__description', id: descriptionId, text: String(description) })
    : null;

  const closeButton = el(
    'button',
    {
      type: 'button',
      class: 'btn btn--ghost btn--icon',
      attrs: { 'aria-label': 'Close dialog', title: 'Close' },
    },
    [icon('close', { size: 18 })],
  );

  const body = el('div', { class: 'dialog__body' });
  append(body, content);

  const buttons = actions.map((action) => el(
    'button',
    {
      type: 'button',
      class: ['btn', action.className],
      disabled: action.disabled === true,
    },
    [String(action.label ?? '')],
  ));

  const element = el(
    'dialog',
    {
      class: ['dialog', Object.hasOwn(SIZE_CLASS, size) ? SIZE_CLASS[size] : SIZE_CLASS.narrow],
      attrs: {
        'aria-labelledby': headingId,
        'aria-describedby': descriptionNode ? descriptionId : null,
      },
    },
    [
      el('div', { class: 'dialog__header' }, [
        el('div', { class: 'spacer' }, [heading, descriptionNode]),
        closeButton,
      ]),
      body,
      buttons.length ? el('div', { class: 'dialog__footer' }, buttons) : null,
    ],
  );

  disposers.push(on(closeButton, 'click', () => close(null)));

  buttons.forEach((button, index) => {
    const action = actions[index];
    disposers.push(on(button, 'click', () => {
      if (typeof action.onSelect === 'function') {
        const verdict = action.onSelect({ close, element, body });
        if (verdict === false) return;
      }
      close(action.value === undefined ? null : action.value);
    }));
  });

  // `showModal()` already keeps focus inside the top layer in modern browsers;
  // trapFocus makes the behaviour explicit and identical to the panels.
  disposers.push(trapFocus(element));
  disposers.push(on(element, 'close', teardown));
  // Escape fires `cancel` first - treat it exactly like the Cancel button.
  disposers.push(on(element, 'cancel', () => {
    outcome = null;
  }));
  disposers.push(on(element, 'click', (event) => {
    if (event.target === element) close(null);
  }));

  document.body.appendChild(element);
  element.showModal();

  const autofocusIndex = actions.findIndex((action) => action.autofocus === true);
  const initialFocus = (autofocusIndex >= 0 ? buttons[autofocusIndex] : null)
    ?? firstFocusableInBody(body)
    ?? closeButton;
  initialFocus.focus();

  return { element, body, buttons, close, closed };
}

/**
 * Ask a yes/no question and resolve to the answer.
 *
 * Used wherever an action is destructive or irreversible (resetting Lunchly,
 * clearing a day, deleting a staff rule). Returning a promise keeps the calling
 * code linear - `if (!(await confirmDialog(...))) return;` - instead of forcing
 * every caller into a callback.
 *
 * For `tone: 'danger'` the confirm button becomes `.btn--danger-solid` and
 * initial focus deliberately lands on **Cancel**, so a reflexive Enter cannot
 * destroy anything.
 *
 * @param {object} options
 * @param {string} options.title
 * @param {string} [options.message] - one sentence explaining the consequence.
 * @param {string} [options.confirmLabel='Confirm']
 * @param {string} [options.cancelLabel='Cancel']
 * @param {'default'|'danger'} [options.tone='default']
 * @returns {Promise<boolean>} true only when the confirm button was chosen.
 */
export function confirmDialog({
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
} = {}) {
  const danger = tone === 'danger';
  const messageId = nextId('confirm-message');
  const messageNode = message ? el('p', { id: messageId, text: String(message) }) : null;

  const modal = openModal({
    title,
    size: 'narrow',
    content: messageNode,
    actions: [
      { label: cancelLabel, value: false, autofocus: danger },
      {
        label: confirmLabel,
        value: true,
        className: danger ? 'btn--danger-solid' : 'btn--primary',
        autofocus: !danger,
      },
    ],
  });

  if (messageNode) modal.element.setAttribute('aria-describedby', messageId);

  return modal.closed.then((value) => value === true);
}

/**
 * Ask for a single line of text and resolve to it, or to `null` when cancelled.
 *
 * This is how Lunchly captures the *reason* a manager gives when overriding a
 * warning: the reason is stored on the assignment, so an empty one would make
 * the audit trail worthless. The confirm button therefore stays disabled until
 * something has been typed (unless `required` is turned off), and Enter in the
 * field submits so the whole interaction is one keystroke.
 *
 * @param {object} options
 * @param {string} [options.title='Add a reason']
 * @param {string} [options.message] - context shown above the field.
 * @param {string} [options.label='Reason'] - the field's visible label.
 * @param {string} [options.placeholder='']
 * @param {string} [options.confirmLabel='Save']
 * @param {string} [options.cancelLabel='Cancel']
 * @param {boolean} [options.required=true] - block confirmation while empty.
 * @returns {Promise<string|null>} the trimmed text, or null when dismissed.
 */
export function promptDialog({
  title = 'Add a reason',
  message = '',
  label = 'Reason',
  placeholder = '',
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  required = true,
} = {}) {
  const inputId = nextId('prompt-input');
  const messageId = `${inputId}-message`;

  const input = el('input', {
    type: 'text',
    id: inputId,
    class: 'input',
    placeholder: String(placeholder ?? ''),
    autocomplete: 'off',
  });

  const messageNode = message
    ? el('p', { class: 'muted', id: messageId, text: String(message) })
    : null;

  const modal = openModal({
    title,
    size: 'narrow',
    content: el('div', { class: 'stack' }, [
      messageNode,
      field({ id: inputId, label, control: input }),
    ]),
    actions: [
      { label: cancelLabel, value: false },
      { label: confirmLabel, value: true, className: 'btn--primary', disabled: required },
    ],
  });

  const confirmButton = modal.buttons[1];
  const syncConfirm = () => {
    confirmButton.disabled = required && input.value.trim() === '';
  };

  // These listeners live and die with the input node, which openModal removes.
  input.addEventListener('input', syncConfirm);
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!confirmButton.disabled) modal.close(true);
  });
  syncConfirm();

  if (messageNode) modal.element.setAttribute('aria-describedby', messageId);

  return modal.closed.then((value) => (value === true ? input.value.trim() : null));
}
